import { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Edit2, Loader2, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { businessApi } from "../../../services/api";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import {
  Button, ConfirmDialog, Dialog, EmptyState, ErrorState, Field, Input, Notice, PageHeader, Pagination, SkeletonRows, Status, plainError,
  Th, THead,
} from "../../../components/ui";
import { focusRing } from "../../../components/ui/cn";
import RequestDecisionDialog from "./components/RequestDecisionDialog";
import { REQUEST_STATUS, describeLeave } from "./myHrText";
import ShopSelect from "../../../components/ShopSelect";

/* ─── Constants ─────────────────────────────────────────── */

const TABS = [
  { key: "requests", label: "Requests" },
  { key: "balances", label: "Balances" },
  { key: "calendar", label: "Calendar" },
  { key: "types", label: "Leave types" },
];


/* ─── Leave Type Form Modal ─────────────────────────────── */

function LeaveTypeModal({ open, onClose, onSubmit, initial }) {
  const [form, setForm] = useState({
    name: "", code: "", default_balance_days: 0,
    requires_approval: 1, is_paid: 1, carry_over_max_days: 0,
  });
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name || "",
              code: initial.code || "",
              default_balance_days: initial.default_balance_days ?? 0,
              requires_approval: initial.requires_approval ?? 1,
              is_paid: initial.is_paid ?? 1,
              carry_over_max_days: initial.carry_over_max_days ?? 0,
            }
          : { name: "", code: "", default_balance_days: 0, requires_approval: 1, is_paid: 1, carry_over_max_days: 0 },
      );
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => () => setForm((f) => ({ ...f, [key]: f[key] ? 0 : 1 }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!editing && !form.code.trim())) return;
    setSaving(true);
    try { await onSubmit(form); } finally { setSaving(false); }
  };

  const inputCls = "h-control w-full rounded-control border border-default bg-surface px-3 text-body text-foreground placeholder:text-tertiary hover:border-strong focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-touch";

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next && !saving) onClose(); }}
      title={editing ? "Edit leave type" : "New leave type"}
      size="md"
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="leave-type-form" variant="primary" loading={saving}>
            {editing ? "Save changes" : "Create leave type"}
          </Button>
        </>
      )}
    >
      <form id="leave-type-form" onSubmit={handleSubmit} className="text-body-sm text-foreground">

        <label className="mb-1 block text-body-sm font-medium">Name *</label>
        <input value={form.name} onChange={set("name")} required maxLength={100} className={`mb-3 ${inputCls}`} />

        {!editing && (
          <>
            <label className="mb-1 block text-body-sm font-medium">Code *</label>
            <input value={form.code} onChange={set("code")} required maxLength={30} placeholder="e.g. VACATION" className={`mb-3 ${inputCls}`} />
          </>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="mb-1 block text-body-sm font-medium">Default Balance (days)</label>
            <input type="number" min="0" max="365" value={form.default_balance_days} onChange={set("default_balance_days")} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-body-sm font-medium">Max Carry-Over (days)</label>
            <input type="number" min="0" max="365" value={form.carry_over_max_days} onChange={set("carry_over_max_days")} className={inputCls} />
          </div>
        </div>

        <div className="mb-4 flex items-center gap-6">
          <label className="flex items-center gap-2 text-body-sm">
            <input type="checkbox" checked={!!form.is_paid} onChange={toggle("is_paid")} className="rounded" />
            Paid leave
          </label>
        </div>

      </form>
    </Dialog>
  );
}

/* ─── Leave Request Modal ───────────────────────────────── */

/* ─── Leave Types Tab ───────────────────────────────────── */

function LeaveTypesTab({ leaveTypes, loading, onReload, canManageLeaveTypes }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const handleSubmit = async (data) => {
    if (editing?.id) {
      await businessApi.updateLeaveType(editing.id, data);
    } else {
      await businessApi.createLeaveType(data);
    }
    setShowForm(false);
    setEditing(null);
    onReload();
  };

  const [deactivating, setDeactivating] = useState(null);
  // APP-HR-080: a leave type nobody has used can be deleted. The server proves it again when asked.
  const [deleting, setDeleting] = useState(null);
  const [failure, setFailure] = useState("");
  const toggleActive = async (lt) => {
    if (lt.is_active) { setDeactivating(lt); return; }
    setFailure("");
    try {
      await businessApi.updateLeaveType(lt.id, { is_active: 1 });
      onReload();
    } catch (err) {
      setFailure(plainError(err, "The leave type could not be activated. Try again."));
    }
  };

  return (
    <>
      {failure ? <Notice tone="danger" role="alert" className="mb-3">{failure}</Notice> : null}
      {deactivating ? (
        <ConfirmDialog
          title={`Deactivate ${deactivating.name}?`}
          tone="danger"
          confirmLabel="Deactivate leave type"
          cancelLabel="Keep it active"
          failureTitle="Not deactivated"
          facts={[{ label: "Leave type", value: `${deactivating.name}${deactivating.is_paid ? " · paid" : " · unpaid"}` }]}
          consequence="Employees can no longer request it. Leave already requested or approved and existing balances are kept."
          onClose={() => setDeactivating(null)}
          onConfirm={async () => {
            await businessApi.updateLeaveType(deactivating.id, { is_active: 0 });
            setDeactivating(null);
            onReload();
          }}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title={`Delete ${deleting.name}?`}
          tone="danger"
          confirmLabel="Delete leave type"
          cancelLabel="Keep leave type"
          failureTitle="Not deleted"
          facts={[{ label: "Leave type", value: `${deleting.name}${deleting.is_paid ? " · paid" : " · unpaid"}` }]}
          consequence="Nobody has requested this leave type or holds a balance of it, so it is removed for good and its code becomes free. This cannot be undone. A leave type that has been used cannot be deleted; it is deactivated instead."
          onClose={() => setDeleting(null)}
          onConfirm={async () => {
            await businessApi.deleteLeaveType(deleting.id);
            setDeleting(null);
            onReload();
          }}
        />
      ) : null}
      <div className="mb-4 flex justify-end">
        {canManageLeaveTypes ? (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="business-primary-action inline-flex items-center gap-2 rounded-control bg-accent px-4 py-2 text-body-sm font-medium text-on-accent hover:bg-accent-hover">
            <Plus className="h-4 w-4" /> New Leave Type
          </button>
        ) : (
          <div className="rounded-control border border-default bg-surface-sunken px-3 py-2 text-meta text-secondary">
            Read-only leave type access.
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>
      ) : leaveTypes.length === 0 ? (
        <div className="py-12 text-center text-body-sm text-secondary">No leave types defined yet.</div>
      ) : (
        <div className="relative overflow-x-auto rounded-panel border border-default bg-surface">
          <table data-ui="table" className="w-full text-left text-cell text-foreground">
            <THead>
              <tr>
                <Th className="!px-3">Name</Th>
                <Th className="!px-3">Code</Th>
                <Th className="!px-3 text-center">Default Days</Th>
                <Th className="!px-3 text-center">Carry-Over Max</Th>
                <Th className="!px-3 text-center">Paid</Th>
                <Th className="!px-3 text-center">Active</Th>
                <Th numeric className="!px-3">Actions</Th>
              </tr>
            </THead>
            <tbody className="divide-y">
              {leaveTypes.map((lt) => (
                <tr key={lt.id} className="hover:bg-hover">
                  <td className="px-3 py-2 font-medium">{lt.name}</td>
                  <td className="px-3 py-2">
                    <span className="rounded-control bg-surface-sunken px-1.5 py-0.5 text-meta tabular-nums text-secondary">{lt.code}</span>
                  </td>
                  <td className="px-3 py-2 text-center">{lt.default_balance_days}</td>
                  <td className="px-3 py-2 text-center">{lt.carry_over_max_days}</td>
                  <td className="px-3 py-2 text-center">{lt.is_paid ? <span className="text-success">Yes</span> : <span className="text-tertiary">No</span>}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={lt.is_active ? "text-foreground" : "text-secondary"}>{lt.is_active ? "Active" : "Inactive"}</span>
                    {canManageLeaveTypes ? (
                      <Button size="sm" variant="quiet" className="ml-2" onClick={() => toggleActive(lt)}>{lt.is_active ? "Deactivate" : "Activate"}</Button>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canManageLeaveTypes ? (
                      <span className="inline-flex gap-1">
                        <button onClick={() => { setEditing(lt); setShowForm(true); }} className="rounded p-1 text-secondary hover:bg-hover hover:text-accent" title="Edit" aria-label={`Edit ${lt.name}`}>
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {Number(lt.never_used) === 1 ? (
                          <button onClick={() => setDeleting(lt)} className="rounded p-1 text-secondary hover:bg-hover hover:text-danger" title="Delete (never used)" aria-label={`Delete ${lt.name}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <LeaveTypeModal open={showForm} onClose={() => { setShowForm(false); setEditing(null); }} onSubmit={handleSubmit} initial={editing} />
    </>
  );
}

/* ─── Leave Requests Tab ────────────────────────────────── */

const LEAVE_FILTERS = [
  { value: "pending", label: "Waiting for review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
  { value: "", label: "All requests" },
];
const LEAVE_TONE = { approved: "success", rejected: "danger" };
const LEAVE_DONE = { approve: "approved", reject: "rejected", cancel: "cancelled" };

function RequestsTab({ canManageLeaveRequests, currentMembershipId, isOwner, onChanged }) {
  const [state, setState] = useState({ status: "loading", rows: [], total: 0 });
  // The work comes first: requests waiting for a decision.
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [deciding, setDeciding] = useState(null); // { request, action }
  const [notice, setNotice] = useState("");
  const limit = 20;

  const loadRequests = useCallback(async () => {
    try {
      const params = { page, limit };
      if (statusFilter) params.status = statusFilter;
      const res = await businessApi.getLeaveRequests(params);
      setState({ status: "ready", rows: res?.rows || [], total: Number(res?.total || 0) });
    } catch {
      setState({ status: "error", rows: [], total: 0 });
    }
  }, [page, statusFilter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const isOwn = (request) => Number(currentMembershipId || 0) > 0 && Number(request.business_member_id) === Number(currentMembershipId);
  const employee = (request) => request.member_name || `Employee #${request.business_member_id}`;
  const days = (request) => `${request.total_days} working day${Number(request.total_days) === 1 ? "" : "s"}`;

  // Decisions (Batch 4): reject and cancel need a reason the employee sees; nobody decides their own leave; the owner
  // is asked for a reason when no one else can review it (APR-4). The server decides all of it.
  const submit = async ({ note, overrideReason }) => {
    const { request, action } = deciding;
    const body = { review_notes: note || undefined };
    const extra = overrideReason ? { override_reason: overrideReason } : {};
    if (action === "approve") await businessApi.approveLeaveRequest(request.id, { ...body, ...extra });
    else if (action === "reject") await businessApi.rejectLeaveRequest(request.id, { ...body, ...extra });
    else await businessApi.cancelLeaveRequest(request.id, body);
    setDeciding(null);
    setNotice(`${employee(request)}'s leave was ${LEAVE_DONE[action]}. They can see it in My HR.`);
    loadRequests();
    onChanged?.();
  };

  const dialog = () => {
    const { request, action } = deciding;
    const facts = [
      { label: "Employee", value: employee(request) },
      { label: "Leave", value: request.leave_type_name },
      { label: "Dates", value: describeLeave(request) },
    ];
    const shared = { facts, onSubmit: submit, onClose: () => { setDeciding(null); loadRequests(); } };
    const theirReason = request.reason ? <p>Their reason: {request.reason}</p> : null;
    if (action === "approve") {
      return (
        <RequestDecisionDialog
          {...shared}
          title={`Approve ${employee(request)}'s leave?`}
          confirmLabel="Approve leave"
          consequence={`${days(request)} of ${request.leave_type_name} ${Number(request.total_days) === 1 ? "is" : "are"} taken from their balance and counted as leave in attendance and payroll.`}
          reason={{ label: "Note for the employee (optional)" }}
          failureTitle="Not approved"
        >
          {theirReason}
        </RequestDecisionDialog>
      );
    }
    if (action === "reject") {
      return (
        <RequestDecisionDialog
          {...shared}
          title={`Reject ${employee(request)}'s leave?`}
          confirmLabel="Reject leave"
          consequence="No days are taken from their balance. They can ask again."
          reason={{ label: "Why? The employee sees this.", required: true }}
          failureTitle="Not rejected"
        >
          {theirReason}
        </RequestDecisionDialog>
      );
    }
    const approved = request.status === "approved";
    return (
      <RequestDecisionDialog
        {...shared}
        tone="danger"
        title={`Cancel ${employee(request)}'s ${approved ? "approved leave" : "leave request"}?`}
        confirmLabel="Cancel leave"
        cancelLabel={approved ? "Keep the leave" : "Keep the request"}
        consequence={approved ? "The leave no longer applies and the days go back to their balance." : "The request will not be reviewed."}
        reason={{ label: "Why? The employee sees this.", required: true }}
        failureTitle="Not cancelled"
      >
        {theirReason}
      </RequestDecisionDialog>
    );
  };

  const { status, rows, total } = state;
  const totalPages = Math.ceil(total / limit);
  const panel = "rounded-panel border border-default bg-surface";

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-56 max-sm:w-full">
          <ShopSelect
            ariaLabel="Show leave requests"
            value={statusFilter}
            onValueChange={(value) => { setStatusFilter(value); setPage(1); }}
            options={LEAVE_FILTERS}
            triggerClassName="h-control w-full rounded-control px-3 text-body"
            minPanelWidth={224}
          />
        </div>
        <Link to="/business/hr/me/leave" className={`inline-flex items-center rounded-control text-body-sm font-medium text-accent hover:underline max-md:min-h-touch ${focusRing}`}>Your own leave is in My HR</Link>
      </div>

      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      {status === "loading" ? (
        <div className={panel}><SkeletonRows rows={4} columns={3} label="Loading leave requests" /></div>
      ) : status === "error" ? (
        <div className={panel}><ErrorState title="Leave requests couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={loadRequests} /></div>
      ) : rows.length === 0 ? (
        <div className={panel}>
          <EmptyState title={statusFilter === "pending" ? "No leave requests are waiting" : "No leave requests here"} description={statusFilter === "pending" ? "New requests from employees appear here for a decision." : undefined} />
        </div>
      ) : (
        <ul aria-label="Leave requests" className={`${panel} divide-y divide-subtle`}>
          {rows.map((r) => {
            const own = isOwn(r);
            const canDecide = canManageLeaveRequests && r.status === "pending" && (!own || isOwner);
            const canCancelRequest = canManageLeaveRequests && !own && (r.status === "pending" || r.status === "approved");
            const label = REQUEST_STATUS[r.status]?.label || "Waiting for review";
            const tone = LEAVE_TONE[r.status] || "neutral";
            return (
              <li key={r.id} className="flex flex-col gap-2 px-4 py-3 text-body-sm lg:flex-row lg:items-start lg:justify-between lg:gap-6">
                <div className="min-w-0 space-y-0.5">
                  <p className="text-panel-title text-foreground">{employee(r)}{own ? " (you)" : ""}</p>
                  <p className="text-foreground">{r.leave_type_name} · {describeLeave(r)}</p>
                  {r.reason ? <p className="text-secondary">Reason: {r.reason}</p> : null}
                  <p>
                    <Status tone={tone} icon={tone !== "neutral"} className={tone === "neutral" ? "text-secondary" : "font-medium"}>{label}</Status>
                    {r.status !== "pending" && r.reviewer_name ? <span className="text-secondary"> by {r.reviewer_name}{r.owner_override ? " (owner decision)" : ""}</span> : null}
                    {r.leave_number ? <span className="text-meta text-tertiary"> · {r.leave_number}</span> : null}
                  </p>
                </div>
                {own && r.status === "pending" && !isOwner ? (
                  <p className="text-meta text-secondary lg:text-right">Your own request — someone else reviews it.</p>
                ) : canDecide || canCancelRequest ? (
                  <div className="flex shrink-0 flex-wrap gap-2 lg:justify-end">
                    {canDecide ? <Button size="sm" variant="primary" onClick={() => setDeciding({ request: r, action: "approve" })}>Approve</Button> : null}
                    {canDecide ? <Button size="sm" onClick={() => setDeciding({ request: r, action: "reject" })}>Reject</Button> : null}
                    {canCancelRequest ? <Button size="sm" variant="quiet" onClick={() => setDeciding({ request: r, action: "cancel" })}>Cancel leave</Button> : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label="Leave request pages" />
      {deciding ? dialog() : null}
    </div>
  );
}

/* ─── Leave Balances Tab ────────────────────────────────── */

// C3: an entitlement the employee can spend and payroll honours. The form states whose, which leave and which year;
// the person types the figures, so no second prompt follows.
function BalanceDialog({ leaveType, employee, year, existing, onClose, onSave }) {
  const [total, setTotal] = useState(String(existing?.total_days ?? 0));
  const [carried, setCarried] = useState(String(existing?.carried_over_days ?? 0));
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const valid = [total, carried].every((value) => value !== "" && Number(value) >= 0 && Number(value) <= 365);
  const submit = async (event) => {
    event.preventDefault();
    if (pending || !valid) return;
    setPending(true);
    setFailure("");
    try {
      await onSave({ total_days: Number(total), carried_over_days: Number(carried) });
    } catch (err) {
      setFailure(plainError(err, "The balance could not be saved. Try again."));
      setPending(false);
    }
  };
  return (
    <Dialog
      open
      size="sm"
      onOpenChange={(next) => { if (!next && !pending) onClose(); }}
      title={`Set ${employee}'s ${leaveType.name} balance`}
      description={`For ${year}. Days already used stay as they are.`}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>Cancel</Button>
          <Button type="submit" form="leave-balance-form" variant="primary" loading={pending} disabled={!valid}>Save balance</Button>
        </>
      }
    >
      <form id="leave-balance-form" onSubmit={submit} noValidate className="space-y-3">
        <Field label="Days for the year" hint="0 to 365." required>
          <Input type="number" min="0" max="365" step="0.5" value={total} disabled={pending} onChange={(event) => setTotal(event.target.value)} />
        </Field>
        <Field label="Days carried over from last year" hint="0 to 365." required>
          <Input type="number" min="0" max="365" step="0.5" value={carried} disabled={pending} onChange={(event) => setCarried(event.target.value)} />
        </Field>
        {failure ? <Notice tone="danger" title="Not saved" role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}

function BalancesTab({ leaveTypes, members, canManageLeaveBalances }) {
  const [selectedMember, setSelectedMember] = useState("");
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadBalances = useCallback(async () => {
    if (!selectedMember) { setBalances([]); return; }
    setLoading(true);
    try {
      const res = await businessApi.getLeaveBalances(selectedMember, { year });
      setBalances(res.balances);
    } catch (err) {
      console.error("Failed to load balances:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedMember, year]);

  useEffect(() => { loadBalances(); }, [loadBalances]);

  const [editingBalance, setEditingBalance] = useState(null); // leave type
  const memberName = members.find((member) => String(member.id) === String(selectedMember))?.name || "this employee";

  return (
    <div className="space-y-4">
      {editingBalance ? (
        <BalanceDialog
          leaveType={editingBalance}
          employee={memberName}
          year={year}
          existing={balances.find((b) => b.leave_type_id === editingBalance.id)}
          onClose={() => setEditingBalance(null)}
          onSave={async (values) => {
            await businessApi.setLeaveBalance(selectedMember, editingBalance.id, { ...values, year: Number(year) });
            setEditingBalance(null);
            loadBalances();
          }}
        />
      ) : null}
      {/* Selectors */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem]">
          <label className="mb-1 block text-body-sm font-medium">Employee</label>
          <ShopSelect
            value={selectedMember}
            onValueChange={setSelectedMember}
            options={[
              { value: "", label: "— Select Employee —" },
              ...members.map((member) => ({ value: member.id, label: member.name || [member.firstName, member.lastName].filter(Boolean).join(" ") || `#${member.id}` })),
            ]}
            ariaLabel="Select employee"
            align="start"
            minPanelWidth={224}
            triggerClassName="min-h-10 w-full rounded-control px-3 py-2 text-body-sm"
            panelClassName="py-1"
          />
        </div>
        <div>
          <label className="mb-1 block text-body-sm font-medium">Year</label>
          <ShopSelect
            value={year}
            onValueChange={setYear}
            options={[0, -1, -2, 1].map((offset) => {
              const optionYear = new Date().getFullYear() + offset;
              return { value: optionYear, label: String(optionYear) };
            })}
            ariaLabel="Select leave balance year"
            align="start"
            minPanelWidth={128}
            triggerClassName="min-h-10 w-32 rounded-control px-3 py-2 text-body-sm"
            panelClassName="py-1"
          />
        </div>
      </div>

      {!selectedMember ? (
        <div className="py-12 text-center text-body-sm text-secondary">Select an employee to view their leave balances.</div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>
      ) : (
        <div className="relative overflow-x-auto rounded-panel border border-default bg-surface">
          <table data-ui="table" className="w-full text-left text-cell text-foreground">
            <THead>
              <tr>
                <Th className="!px-3">Leave Type</Th>
                <Th className="!px-3 text-center">Total</Th>
                <Th className="!px-3 text-center">Carried Over</Th>
                <Th className="!px-3 text-center">Used</Th>
                <Th className="!px-3 text-center">Available</Th>
                <Th numeric className="!px-3">Actions</Th>
              </tr>
            </THead>
            <tbody className="divide-y">
              {leaveTypes.filter((lt) => lt.is_active).map((lt) => {
                const b = balances.find((x) => x.leave_type_id === lt.id);
                const total = Number(b?.total_days ?? 0);
                const carried = Number(b?.carried_over_days ?? 0);
                const used = Number(b?.used_days ?? 0);
                const available = total + carried - used;
                return (
                  <tr key={lt.id} className="hover:bg-hover">
                    <td className="px-3 py-2 font-medium">
                      {lt.name}
                      <span className="ml-2 rounded-control bg-surface-sunken px-1.5 py-0.5 text-meta tabular-nums text-secondary">{lt.code}</span>
                    </td>
                    <td className="px-3 py-2 text-center">{total}</td>
                    <td className="px-3 py-2 text-center">{carried}</td>
                    <td className="px-3 py-2 text-center">{used}</td>
                    <td className="px-3 py-2 text-center font-semibold">
                      <span className={available > 0 ? "text-success" : "text-danger"}>
                        {available}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canManageLeaveBalances ? (
                        <Button size="sm" onClick={() => setEditingBalance(lt)}>Set balance</Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Calendar Tab ──────────────────────────────────────── */

const LEAVE_COLORS = [
  "bg-blue-400", "bg-emerald-400", "bg-purple-400", "bg-amber-400",
  "bg-rose-400", "bg-cyan-400", "bg-indigo-400", "bg-lime-400",
];

function CalendarTab({ leaveTypes }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await businessApi.getLeaveRequests({ status: "approved", limit: 500 });
        if (!cancelled) setRequests(res.rows || []);
      } catch (err) {
        console.error("Calendar load failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Build a color map for leave types
  const typeColorMap = {};
  (leaveTypes || []).forEach((lt, i) => {
    typeColorMap[lt.id] = LEAVE_COLORS[i % LEAVE_COLORS.length];
  });

  // Calendar math
  const firstDay = new Date(year, month, 1);
  const startDow = firstDay.getDay(); // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = firstDay.toLocaleString("default", { month: "long", year: "numeric" });

  // Determine which requests overlap each day in this month
  const dayMap = {}; // day (1..31) -> array of request summaries
  for (const req of requests) {
    const rStart = new Date(req.start_date + "T00:00:00");
    const rEnd   = new Date(req.end_date + "T00:00:00");
    for (let d = 1; d <= daysInMonth; d++) {
      const cellDate = new Date(year, month, d);
      if (cellDate >= rStart && cellDate <= rEnd) {
        if (!dayMap[d]) dayMap[d] = [];
        dayMap[d].push({
          name: req.member_name || "Employee",
          typeId: req.leave_type_id,
          typeName: req.leave_type_name || (leaveTypes || []).find((t) => t.id === req.leave_type_id)?.name || "",
        });
      }
    }
  }

  const shiftMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setMonth(m);
    setYear(y);
  };

  const cells = [];
  // Leading blanks
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayDate = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  return (
    <div className="space-y-4">
      {/* Month nav */}
      <div className="flex items-center gap-3">
        <button onClick={() => shiftMonth(-1)} className="rounded-control border p-2"><ChevronLeft className="h-4 w-4" /></button>
        <span className="min-w-[160px] text-center text-section-title font-semibold">{monthLabel}</span>
        <button onClick={() => shiftMonth(1)} className="rounded-control border p-2"><ChevronRight className="h-4 w-4" /></button>
        <button
          onClick={() => { setYear(now.getFullYear()); setMonth(now.getMonth()); }}
          className="rounded-control border px-3 py-2 text-meta"
        >Today</button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>
      ) : (
        <>
          {/* Grid */}
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-panel border border-default bg-surface-sunken">
            {/* Weekday headers */}
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="bg-surface-sunken px-2 py-2 text-center text-meta font-semibold text-secondary">{d}</div>
            ))}
            {/* Day cells */}
            {cells.map((day, idx) => (
              <div
                key={idx}
                className={`min-h-[72px] bg-surface p-1.5 ${
                  !day ? "bg-surface-sunken" : ""
                } ${isCurrentMonth && day === todayDate ? "ring-2 ring-inset ring-focus" : ""}`}
              >
                {day && (
                  <>
                    <div className="text-meta font-medium text-secondary">{day}</div>
                    <div className="mt-0.5 flex flex-col gap-0.5">
                      {(dayMap[day] || []).slice(0, 3).map((entry, i) => (
                        <div
                          key={i}
                          className={`truncate rounded-control px-1 py-0.5 text-meta leading-tight text-on-accent ${typeColorMap[entry.typeId] || "bg-neutral-400"}`}
                          title={`${entry.name} — ${entry.typeName}`}
                        >
                          {entry.name}
                        </div>
                      ))}
                      {(dayMap[day] || []).length > 3 && (
                        <div className="text-meta text-tertiary">+{dayMap[day].length - 3} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-3 text-meta">
            {(leaveTypes || []).filter((lt) => lt.is_active !== 0).map((lt, i) => (
              <div key={lt.id} className="flex items-center gap-1.5">
                <span className={`inline-block h-3 w-3 rounded-sm ${LEAVE_COLORS[i % LEAVE_COLORS.length]}`} />
                <span className="text-secondary">{lt.name}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ─── Main Component ────────────────────────────────────── */

export default function HRLeave() {
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const hasAnyPermission = useCallback((permissionNames = []) => (
    authRole === "OWNER" || permissionNames.some((permissionName) => auth?.hasPermission?.(permissionName))
  ), [auth, authRole]);
  const canReadLeaveTypes = hasAnyPermission(["hr:leave_types:read", "hr:leaves:read", "hr:leaves:manage"]);
  const canReadLeaveRequests = hasAnyPermission(["hr:leave_requests:read", "hr:leaves:read", "hr:leaves:manage"]);
  const canReadLeaveBalances = hasAnyPermission(["hr:leave_balances:read", "hr:leaves:read", "hr:leaves:manage"]);
  const canManageLeaveTypes = hasAnyPermission(["hr:leave_types:manage", "hr:leaves:manage"]);
  const canManageLeaveRequests = hasAnyPermission(["hr:leave_requests:review", "hr:leaves:manage"]);
  const canRequestLeave = hasAnyPermission(["hr:leaves:request"]);
  const canManageLeaveBalances = hasAnyPermission(["hr:leave_balances:manage", "hr:leaves:manage"]);
  const canAccessLeavePage = canReadLeaveTypes || canReadLeaveRequests || canReadLeaveBalances || canManageLeaveTypes || canManageLeaveRequests || canManageLeaveBalances || canRequestLeave;
  const isSelfServiceOnly = canRequestLeave && !canReadLeaveTypes && !canReadLeaveRequests && !canReadLeaveBalances && !canManageLeaveTypes && !canManageLeaveRequests && !canManageLeaveBalances;
  const currentMembershipId = Number(auth?.currentBusiness?.membership_id || 0) || null;
  const [tab, setTab] = useState(canReadLeaveRequests || canManageLeaveRequests ? "requests" : "types");
  const [pendingCount, setPendingCount] = useState(null);
  const refreshPending = useCallback(() => {
    if (!(canReadLeaveRequests || canManageLeaveRequests)) return;
    businessApi.getLeaveRequests({ status: "pending", limit: 1 }).then((res) => setPendingCount(Number(res?.total || 0))).catch(() => {});
  }, [canReadLeaveRequests, canManageLeaveRequests]);
  useEffect(() => { refreshPending(); }, [refreshPending]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!canAccessLeavePage) {
      setLeaveTypes([]);
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [typesRes, membersRes] = await Promise.all([
        (canReadLeaveTypes || canManageLeaveTypes || canReadLeaveRequests || canManageLeaveRequests || canReadLeaveBalances || canManageLeaveBalances || canRequestLeave)
          ? businessApi.getLeaveTypes().catch(() => ({ leaveTypes: [] }))
          : Promise.resolve({ leaveTypes: [] }),
        (canReadLeaveRequests || canManageLeaveRequests || canReadLeaveBalances || canManageLeaveBalances)
          ? businessApi.getMembers().catch(() => ({ members: [] }))
          : Promise.resolve({ members: [] }),
      ]);
      setLeaveTypes(typesRes.leaveTypes || []);
      setMembers(membersRes.members || membersRes.data || []);
    } catch (err) {
      console.error("Failed to load leave data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    canAccessLeavePage,
    canManageLeaveBalances,
    canManageLeaveRequests,
    canManageLeaveTypes,
    canReadLeaveBalances,
    canReadLeaveRequests,
    canReadLeaveTypes,
    canRequestLeave,
  ]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isSelfServiceOnly) {
      setTab("requests");
    }
  }, [isSelfServiceOnly]);

  const visibleTabs = TABS;

  const reloadTypes = async () => {
    if (!(canReadLeaveTypes || canManageLeaveTypes || canRequestLeave)) {
      setLeaveTypes([]);
      return;
    }
    const res = await businessApi.getLeaveTypes();
    setLeaveTypes(res.leaveTypes);
  };

  // Your own leave (balances, requests) lives in My HR; this page is for people who manage leave.
  if (isSelfServiceOnly || !canAccessLeavePage) return <Navigate to="/business/hr/me/leave" replace />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Leave"
        description="Decide employees' leave requests, keep balances right, and manage the leave types this business offers."
        navigation={
          <div role="tablist" aria-label="Leave sections" className="-mb-px flex gap-6 overflow-x-auto overflow-y-hidden border-b border-subtle">
            {visibleTabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                data-ui="button"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`relative inline-flex h-10 shrink-0 items-center gap-1.5 text-body-sm font-medium max-md:h-touch after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full ${focusRing} ${tab === key ? "text-foreground after:bg-accent" : "text-secondary after:bg-transparent hover:text-foreground"}`}
              >
                {label}
                {key === "requests" && pendingCount ? <span className="text-meta tabular-nums text-tertiary">{pendingCount}</span> : null}
              </button>
            ))}
          </div>
        }
      />
      <>
        <>

      {/* Tab Content */}
      {loading ? (
        <div className="rounded-panel border border-default bg-surface"><SkeletonRows rows={4} columns={3} label="Loading leave" /></div>
      ) : tab === "types" ? (
        canReadLeaveTypes || canManageLeaveTypes ? (
          <LeaveTypesTab leaveTypes={leaveTypes} loading={false} onReload={reloadTypes} canManageLeaveTypes={canManageLeaveTypes} />
        ) : (
          <Notice tone="warning" title="You can't open this section">It is visible only to people who manage leave type configuration. Ask the owner if you need access.</Notice>
        )
      ) : tab === "requests" ? (
        canReadLeaveRequests || canManageLeaveRequests || canRequestLeave ? (
          <RequestsTab
            canManageLeaveRequests={canManageLeaveRequests}
            currentMembershipId={currentMembershipId}
            isOwner={authRole === "OWNER"}
            onChanged={refreshPending}
          />
        ) : (
          <Notice tone="warning" title="You can't open this section">It is visible only to people who manage leave requests. Ask the owner if you need access.</Notice>
        )
      ) : tab === "balances" ? (
        canReadLeaveBalances || canManageLeaveBalances ? (
          <BalancesTab leaveTypes={leaveTypes} members={members} canManageLeaveBalances={canManageLeaveBalances} />
        ) : (
          <Notice tone="warning" title="You can't open this section">It is visible only to people who manage leave balances. Ask the owner if you need access.</Notice>
        )
      ) : (
        <CalendarTab leaveTypes={leaveTypes} />
      )}
        </>
      </>
    </div>
  );
}
